import React from 'react';
import { Layout } from '../components/common/Layout';
import { MessageCircle, Users, Calendar, Mic } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';

/**
 * Community page redesigned according to specifications
 * Features Discord integration with modern design and clear call-to-actions
 */
const CommunityPage: React.FC = () => {
  const { getCurrentTier } = useSubscription();
  const isFreeTier = getCurrentTier() === 'free';
  
  const discordInviteUrl = 'https://discord.gg/WUHyEZHnS9';
  const discordCommunityUrl = 'https://discord.com/channels/1292047332558086154';

  return (
    <Layout showHeader showFooter>
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50">
        {/* Hero Section */}
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-montserrat font-bold text-neutral-900 mb-6">
              Community
            </h1>
            <h2 className="text-3xl md:text-4xl font-montserrat font-semibold text-primary-600 mb-8">
              Your AI Learning Community
            </h2>
            <p className="text-xl text-neutral-700 mb-12 max-w-2xl mx-auto leading-relaxed">
              Connect, share, and grow with other trAInedup.ai members in our private Discord space.
            </p>
            
            {/* CTA Buttons - Different for paid vs free users */}
            {isFreeTier ? (
              <a
                href={discordInviteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-7 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-lg font-semibold rounded-lg shadow-lg hover:from-primary-600 hover:to-primary-700 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                👉 Join now
              </a>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href={discordInviteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-7 py-4 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white text-lg font-semibold rounded-lg shadow-lg hover:from-secondary-600 hover:to-secondary-700 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  📨 Join Community (Invite)
                </a>
                <a
                  href={discordCommunityUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-7 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-lg font-semibold rounded-lg shadow-lg hover:from-primary-600 hover:to-primary-700 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  💬 Go to Community
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Why Join Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-montserrat font-bold text-neutral-900 mb-8 text-center">
              Why Join the Community?
            </h2>
            <p className="text-xl text-neutral-700 mb-12 text-center max-w-3xl mx-auto">
              At trAInedup.ai, learning isn't just about working through courses, it's about connecting with
              others who are on the same journey.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">💬 Ask Questions</h3>
                <p className="text-neutral-600">Get answers and support from peers and trainers.</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-secondary-600" />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">🤝 Share & Collaborate</h3>
                <p className="text-neutral-600">Exchange prompts, tools, and results.</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mic className="w-8 h-8 text-accent-600" />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">🎤 Live Events</h3>
                <p className="text-neutral-600">Join Q&As, discussions, and workshops.</p>
              </div>
            </div>
          </div>
        </div>

        {/* What's Inside Discord Section */}
        <div className="bg-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-montserrat font-bold text-neutral-900 mb-8 text-center">
                What's Inside the Discord?
              </h2>
              <p className="text-xl text-neutral-700 mb-12 text-center">
                Our community space is designed to keep things simple, welcoming, and useful. Here's what
                you'll find once you join:
              </p>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4 p-4 rounded-lg bg-primary-50 hover:bg-primary-100 transition-colors duration-200">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">🏠</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-2">Welcome Hub</h3>
                      <p className="text-neutral-600">Start here with rules, FAQs, and setup info.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4 p-4 rounded-lg bg-secondary-50 hover:bg-secondary-100 transition-colors duration-200">
                    <div className="w-12 h-12 bg-secondary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">💬</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-2">AI Learning Lounge</h3>
                      <p className="text-neutral-600">Chat, share ideas, and test prompts.</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4 p-4 rounded-lg bg-accent-50 hover:bg-accent-100 transition-colors duration-200">
                    <div className="w-12 h-12 bg-accent-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">👥</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-2">Role-Specific Spaces</h3>
                      <p className="text-neutral-600">Tailored channels for your profession.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4 p-4 rounded-lg bg-primary-50 hover:bg-primary-100 transition-colors duration-200">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-2">Events Calendar</h3>
                      <p className="text-neutral-600">Stay updated on live Q&As and training.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How to Join Section */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 py-16 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl text-white font-montserrat font-bold mb-8">
                How to Join
              </h2>
              <p className="text-xl mb-12 text-white opacity-90">
                It only takes a couple of minutes:
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <p className="text-lg text-white">Click the button below to join our Discord server.</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <p className="text-lg text-white">Verify your membership inside Discord.</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                  <p className="text-lg text-white">Say hello in the Welcome Hub!</p>
                </div>
              </div>
              
              <p className="text-lg mb-8 text-white opacity-90">
                ✨ Don't worry if you're new to Discord, we will guide you through it.
              </p>
              
              <a
                href={discordInviteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-7 py-4 bg-white text-primary-600 text-lg font-semibold rounded-lg shadow-lg hover:bg-neutral-50 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                👉 Join the trAInedup.ai Community
              </a>
            </div>
          </div>
        </div>

        {/* Be Part of Something Bigger Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-montserrat font-bold text-neutral-900 mb-8">
              Be Part of Something Bigger
            </h2>
            <p className="text-xl text-neutral-700 mb-12 max-w-3xl mx-auto leading-relaxed">
              Learning AI is easier and more fun when you're part of a community. Whether you're here
              to get quick answers, share your progress, or meet like-minded professionals, you'll always
              have support at your fingertips.
            </p>
            
            <div className="mb-12">
              <h3 className="text-2xl font-semibold text-neutral-900 mb-6">Ready to connect?</h3>
              <a
                href={discordInviteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-7 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-lg font-semibold rounded-lg shadow-lg hover:from-primary-600 hover:to-primary-700 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                👉 Join Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CommunityPage;

